import type { TenantByDomainArgs } from '../../api/types/tenant.types';

import { queryKeys } from '../../queryKeys';
import { useQuery } from '../base';

export type UseTenantByDomainArgs = TenantByDomainArgs;

export default function useTenantByDomain({ domain }: UseTenantByDomainArgs) {
  const { data, isLoading, status } = useQuery({
    enabled: Boolean(domain),
    queryFn: async (context) => {
      const tenant = await queryKeys.tenants
        .byDomain({ domain })
        .queryFn(context);

      return tenant;
    },
    queryKey: queryKeys.tenants.byDomain({ domain }).queryKey,
    // `TenantGuard` stops the whole app on a failed first read, so one blip must not end it
    retry: 2,
    retryDelay: 500,
  });

  return {
    data,
    isLoading,
    // No domain → the query is DISABLED, and a disabled query reports
    // 'pending' forever — a settled non-answer wearing the in-flight label.
    // Consumers key off that label (`TenantGuard` refuses only a failed read,
    // `/admin/rebrand`'s no-tenant chokepoint fires only on a settled status),
    // so a read that can never even start must report itself as failed here,
    // in the one place that knows why it will not run. Only reachable when the
    // build-injected tenant domain is missing (a fresh clone running dev
    // without `.env.local`) — a real hostname is never empty.
    status: domain ? status : 'error',
  };
}
